'use client'

import React from 'react'

import { cn } from '@/lib/utils'

/**
 * The contactless mark, animated: the arcs brighten in turn — nearest the emitter first — so the
 * glyph reads as a field radiating out rather than a static badge. Opacity only, one slow cycle
 * every 2.6s with a long rest between, and flat arcs under `prefers-reduced-motion`.
 *
 * Paths are the 16px Figma `nfc` icon, ordered emitter-outwards so the stagger runs the right way.
 */
const ARCS = [
  'M3.66667 6C4.0652 6.39922 4.33333 7.1454 4.33333 8C4.33333 8.8546 4.0652 9.6008 3.66667 10',
  'M6 4.66667C6.5978 5.33203 7 6.57564 7 8C7 9.42433 6.5978 10.668 6 11.3333',
  'M8.33333 3.33333C9.1304 4.26484 9.66667 6.0059 9.66667 8C9.66667 9.99413 9.1304 11.7351 8.33333 12.6667',
  'M10.3333 2C11.5289 3.19765 12.3333 5.43615 12.3333 8C12.3333 10.5639 11.5289 12.8023 10.3333 14',
]

type Props = {
  size?: number
  /** Freezes the arcs at full strength — for print and export surfaces. */
  still?: boolean
  className?: string
  style?: React.CSSProperties
}

/**
 * The arcs occupy x 3.3–12.7 of the 16px icon box, so the raw glyph carries a wide dead margin on
 * the emitter side. Cropping the viewBox to the ink lets the mark sit tight against its label.
 */
const BOX = { x: 3.2, y: 1.4, w: 9.8, h: 13.2 }

const NfcWaves = ({ size = 18, still = false, className, style }: Props) => (
  <svg
    width={(size * BOX.w) / BOX.h}
    height={size}
    viewBox={`${BOX.x} ${BOX.y} ${BOX.w} ${BOX.h}`}
    fill="none"
    strokeWidth={1.5}
    className={cn('shrink-0', className)}
    style={style}
    aria-hidden
  >
    {ARCS.map((d, i) => (
      <path
        key={d}
        d={d}
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={still ? undefined : 'lc-nfc-wave'}
        style={still ? undefined : { animationDelay: `${i * 0.12}s` }}
      />
    ))}
  </svg>
)

export default React.memo(NfcWaves)
